import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Package, Layers, Calculator, RefreshCw, Percent, CreditCard, TrendingUp, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import pricingConfigService from '../services/pricingConfigService';

const PricingConfig = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('complexity');

  // Buscar configurações de complexidade
  const { data: complexityData, isLoading: complexityLoading } = useQuery({
    queryKey: ['pricing-complexity'],
    queryFn: () => pricingConfigService.getComplexityWeights(),
  });

  // Buscar configurações de materiais
  const { data: materialData } = useQuery({
    queryKey: ['pricing-materials'],
    queryFn: () => pricingConfigService.getMaterialWeights(),
  });

  // Buscar regras de margem
  const { data: marginData, isLoading: marginLoading } = useQuery({
    queryKey: ['pricing-margin-rules'],
    queryFn: () => pricingConfigService.getMarginRules(),
  });

  // Buscar condições de pagamento
  const { data: paymentData, isLoading: paymentLoading } = useQuery({
    queryKey: ['pricing-payment-conditions'],
    queryFn: () => pricingConfigService.getPaymentConditions(),
  });

  // Mutation para atualizar complexidade
  const updateComplexityMutation = useMutation({
    mutationFn: ({ id, data }) => pricingConfigService.updateComplexityWeight(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['pricing-complexity']);
      toast.success('Configuração de complexidade atualizada!');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  // Mutation para atualizar material
  const updateMaterialMutation = useMutation({
    mutationFn: ({ id, data }) => pricingConfigService.updateMaterialWeight(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['pricing-materials']);
      toast.success('Configuração de material atualizada!');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  // Mutation para atualizar regra de margem
  const updateMarginRuleMutation = useMutation({
    mutationFn: ({ id, data }) => pricingConfigService.updateMarginRule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['pricing-margin-rules']);
      toast.success('Regra de margem atualizada!');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  // Mutation para atualizar condição de pagamento
  const updatePaymentConditionMutation = useMutation({
    mutationFn: ({ id, data }) => pricingConfigService.updatePaymentCondition(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['pricing-payment-conditions']);
      toast.success('Condição de pagamento atualizada!');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleComplexityUpdate = (id, field, value) => {
    updateComplexityMutation.mutate({
      id,
      data: { [field]: value }
    });
  };

  const handleMaterialUpdate = (id, field, value) => {
    updateMaterialMutation.mutate({
      id,
      data: { [field]: value }
    });
  };

  const handleMarginRuleUpdate = (id, field, value) => {
    updateMarginRuleMutation.mutate({
      id,
      data: { [field]: value }
    });
  };

  const handlePaymentConditionUpdate = (id, field, value) => {
    updatePaymentConditionMutation.mutate({
      id,
      data: { [field]: value }
    });
  };

  const complexityLevels = complexityData?.data || [];
  const materialWeights = materialData?.data || {};
  const marginRules = marginData?.data || [];
  const paymentConditions = paymentData?.data || [];

  // Agrupar regras de margem por escopo
  const groupedMargins = {
    GLOBAL: marginRules.filter(r => r.scope === 'GLOBAL'),
    CATEGORY: marginRules.filter(r => r.scope === 'CATEGORY'),
    PRODUCT: marginRules.filter(r => r.scope === 'PRODUCT'),
  };

  const categoryLabels = {
    MATERIA_PRIMA: 'Matéria Prima',
    FERRAGENS: 'Ferragens',
    INSUMOS: 'Insumos',
    SERVICOS: 'Serviços',
    LOGISTICA: 'Logística',
    OUTROS: 'Outros'
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Calculator className="w-7 h-7" />
          Central de Preços do Orçamento
        </h1>
        <p className="text-gray-600 mt-1">
          Configure todos os fatores que afetam o preço final do orçamento em um único lugar
        </p>
      </div>

      {/* Fórmula de Cálculo */}
      <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
        <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          Como o preço é calculado:
        </h3>
        <div className="text-sm text-blue-800 space-y-1 font-mono">
          <p>Preço Final = (Custo Base × Pesos Materiais × Complexidade) × (1 + Margem) × (1 + Extra) × Cond.Pagamento</p>
        </div>
        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <div className="bg-white px-3 py-2 rounded border">
            <span className="font-semibold">1. Materiais:</span> Tipo material, ferragem, vidro, acabamento
          </div>
          <div className="bg-white px-3 py-2 rounded border">
            <span className="font-semibold">2. Complexidade:</span> Nível de dificuldade do projeto
          </div>
          <div className="bg-white px-3 py-2 rounded border">
            <span className="font-semibold">3. Margem:</span> Por categoria/produto
          </div>
          <div className="bg-white px-3 py-2 rounded border">
            <span className="font-semibold">4. Pagamento:</span> Desconto/juros por forma de pagamento
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b overflow-x-auto">
        <button
          onClick={() => setActiveTab('complexity')}
          className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
            activeTab === 'complexity'
              ? 'border-b-2 border-green-500 text-green-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Layers className="w-4 h-4 inline mr-2" />
          Complexidade
        </button>
        <button
          onClick={() => setActiveTab('materials')}
          className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
            activeTab === 'materials'
              ? 'border-b-2 border-green-500 text-green-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Package className="w-4 h-4 inline mr-2" />
          Materiais
        </button>
        <button
          onClick={() => setActiveTab('margins')}
          className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
            activeTab === 'margins'
              ? 'border-b-2 border-green-500 text-green-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Percent className="w-4 h-4 inline mr-2" />
          Margens
        </button>
        <button
          onClick={() => setActiveTab('payment')}
          className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
            activeTab === 'payment'
              ? 'border-b-2 border-green-500 text-green-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <CreditCard className="w-4 h-4 inline mr-2" />
          Pagamento
        </button>
      </div>

      {/* Complexidade Tab */}
      {activeTab === 'complexity' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold">Multiplicadores de Complexidade</h2>
              <p className="text-sm text-gray-500">O nível de complexidade multiplica o orçamento final</p>
            </div>
          </div>

          {complexityLoading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
            </div>
          ) : (
            <div className="space-y-4">
              {complexityLevels.map((level) => (
                <div
                  key={level.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                >
                  <div>
                    <div className="font-medium text-gray-900">
                      Nível {level.complexity_level}: {level.level_label}
                    </div>
                    <div className="text-sm text-gray-500">{level.description}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600">Multiplicador:</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.5"
                      max="2"
                      value={level.price_multiplier}
                      onChange={(e) => handleComplexityUpdate(level.id, 'price_multiplier', parseFloat(e.target.value))}
                      className="w-20 px-3 py-2 border rounded-lg text-center font-mono"
                    />
                    <span className="text-sm text-gray-500">
                      ({((parseFloat(level.price_multiplier) - 1) * 100).toFixed(0)}%)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">Como funciona:</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• <strong>0.90</strong> = 10% de desconto (projeto muito simples)</li>
              <li>• <strong>1.00</strong> = preço normal (projeto padrão)</li>
              <li>• <strong>1.20</strong> = 20% de acréscimo (projeto muito complexo)</li>
            </ul>
          </div>
        </div>
      )}

      {/* Materiais Tab */}
      {activeTab === 'materials' && (
        <div className="space-y-6">
          {Object.entries(materialWeights).map(([type, options]) => (
            <div key={type} className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4 capitalize">
                {type === 'material_type' && 'Tipo de Material'}
                {type === 'hardware_type' && 'Tipo de Ferragem'}
                {type === 'has_glass' && 'Vidro'}
                {type === 'door_finish' && 'Acabamento de Porta'}
              </h2>

              <div className="space-y-3">
                {options.map((option) => (
                  <div
                    key={option.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="font-medium text-gray-900">
                      {option.option_label}
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-600">Peso (%):</label>
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        max="50"
                        value={option.weight_percentage}
                        onChange={(e) => handleMaterialUpdate(option.id, 'weight_percentage', parseFloat(e.target.value))}
                        className="w-20 px-3 py-2 border rounded-lg text-center font-mono"
                      />
                      <span className="text-sm text-gray-500">%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="p-4 bg-blue-50 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">Como funciona:</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• O <strong>peso</strong> é somado ao multiplicador final do orçamento</li>
              <li>• Exemplo: Material Premium (10%) + Ferragem Média (3%) = +13% no orçamento</li>
              <li>• Se o orçamento base é R$10.000, com 13% fica R$11.300</li>
            </ul>
          </div>
        </div>
      )}

      {/* Margens Tab */}
      {activeTab === 'margins' && (
        <div className="space-y-6">
          {/* Margem Global */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Percent className="w-5 h-5" />
              Margem Global
            </h2>
            <p className="text-sm text-gray-500 mb-4">Aplicada a todos os itens do orçamento</p>

            {marginLoading ? (
              <div className="text-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
              </div>
            ) : (
              <div className="space-y-3">
                {groupedMargins.GLOBAL.map((rule) => (
                  <div key={rule.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <div className="font-medium text-gray-900">Margem Padrão</div>
                      <div className="text-sm text-gray-500">Aplica a todos os itens</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={rule.margin_type}
                        onChange={(e) => handleMarginRuleUpdate(rule.id, 'margin_type', e.target.value)}
                        className="px-3 py-2 border rounded-lg"
                      >
                        <option value="percentage">Porcentagem</option>
                        <option value="fixed">Valor Fixo</option>
                      </select>
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        max="100"
                        value={rule.margin_value}
                        onChange={(e) => handleMarginRuleUpdate(rule.id, 'margin_value', parseFloat(e.target.value))}
                        className="w-20 px-3 py-2 border rounded-lg text-center font-mono"
                      />
                      <span className="text-sm text-gray-500">
                        {rule.margin_type === 'percentage' ? '%' : 'R$'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Margens por Categoria */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Margens por Categoria
            </h2>
            <p className="text-sm text-gray-500 mb-4">Margens específicas por categoria de produto</p>

            {marginLoading ? (
              <div className="text-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
              </div>
            ) : (
              <div className="space-y-3">
                {groupedMargins.CATEGORY.map((rule) => (
                  <div key={rule.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <div className="font-medium text-gray-900">
                        {categoryLabels[rule.category] || rule.category}
                      </div>
                      <div className="text-sm text-gray-500">Categoria: {rule.category}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={rule.margin_type}
                        onChange={(e) => handleMarginRuleUpdate(rule.id, 'margin_type', e.target.value)}
                        className="px-3 py-2 border rounded-lg"
                      >
                        <option value="percentage">Porcentagem</option>
                        <option value="fixed">Valor Fixo</option>
                      </select>
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        max="100"
                        value={rule.margin_value}
                        onChange={(e) => handleMarginRuleUpdate(rule.id, 'margin_value', parseFloat(e.target.value))}
                        className="w-20 px-3 py-2 border rounded-lg text-center font-mono"
                      />
                      <span className="text-sm text-gray-500">
                        {rule.margin_type === 'percentage' ? '%' : 'R$'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-4 bg-blue-50 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">Como funciona:</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• A <strong>margem</strong> é adicionada ao custo para formar o preço de venda</li>
              <li>• <strong>Porcentagem</strong>: preço = custo × (1 + margem%)</li>
              <li>• <strong>Valor Fixo</strong>: preço = custo + margem_fixa</li>
            </ul>
          </div>
        </div>
      )}

      {/* Pagamento Tab */}
      {activeTab === 'payment' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Condições de Pagamento
            </h2>
            <p className="text-sm text-gray-500 mb-4">Descontos, acréscimos e juros por forma de pagamento</p>

            {paymentLoading ? (
              <div className="text-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
              </div>
            ) : (
              <div className="space-y-3">
                {paymentConditions.map((condition) => (
                  <div key={condition.id} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="font-medium text-gray-900">{condition.name}</div>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={condition.affects_price}
                          onChange={(e) => handlePaymentConditionUpdate(condition.id, 'affects_price', e.target.checked)}
                          className="w-4 h-4 rounded"
                        />
                        <span className="text-sm text-gray-600">Afeta preço</span>
                      </label>
                    </div>

                    {condition.affects_price && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">Desconto (%)</label>
                          <input
                            type="number"
                            step="0.5"
                            min="0"
                            max="100"
                            value={condition.discount_percentage || 0}
                            onChange={(e) => handlePaymentConditionUpdate(condition.id, 'discount_percentage', parseFloat(e.target.value))}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">Acréscimo (%)</label>
                          <input
                            type="number"
                            step="0.5"
                            min="0"
                            max="100"
                            value={condition.increase_percentage || 0}
                            onChange={(e) => handlePaymentConditionUpdate(condition.id, 'increase_percentage', parseFloat(e.target.value))}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">Juros por Parcela (%)</label>
                          <div className="flex gap-2">
                            <input
                              type="number"
                              step="0.1"
                              min="0"
                              max="10"
                              value={condition.interest_rate_per_installment || 0}
                              onChange={(e) => handlePaymentConditionUpdate(condition.id, 'interest_rate_per_installment', parseFloat(e.target.value))}
                              className="w-20 px-3 py-2 border rounded-lg"
                              disabled={!condition.has_interest}
                            />
                            <label className="flex items-center gap-1 text-sm">
                              <input
                                type="checkbox"
                                checked={condition.has_interest}
                                onChange={(e) => handlePaymentConditionUpdate(condition.id, 'has_interest', e.target.checked)}
                                className="w-4 h-4 rounded"
                              />
                              <span className="text-gray-600">Tem juros</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-4 bg-blue-50 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">Como funciona:</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• <strong>Desconto</strong>: reduz o preço final (ex: à vista = -5%)</li>
              <li>• <strong>Acréscimo</strong>: aumenta o preço (ex: parcelado = +10%)</li>
              <li>• <strong>Juros</strong>: aplicado por parcela do cartão de crédito</li>
              <li>• Exemplo: R$10.000 com 5% desconto = R$9.500</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingConfig;